import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Domains we reject client-side; server enforces the same list.
const FREE_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com',
  'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'protonmail.com', 'proton.me', 'zoho.com', 'gmx.com',
  'mail.com', 'yandex.com', 'yandex.ru', 'rediffmail.com',
  'qq.com', '163.com', '126.com', 'naver.com',
]);

const isBusinessEmail = (email) => {
  if (!email || !email.includes('@')) return false;
  const dom = email.split('@').pop().trim().toLowerCase();
  if (!dom.includes('.')) return false;
  return !FREE_DOMAINS.has(dom);
};

const GetStartedDialog = ({ open, onOpenChange, source = 'get-started', title, subtitle }) => {
  const [form, setForm] = useState({ name: '', company: '', email: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverMessage, setServerMessage] = useState('');

  useEffect(() => {
    if (!open) {
      // Reset after close animation
      const t = setTimeout(() => {
        setForm({ name: '', company: '', email: '' });
        setErrors({});
        setStatus('idle');
        setServerMessage('');
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = 'Please enter your name';
    if (!form.company.trim()) er.company = 'Please enter your company name';
    const email = form.email.trim();
    if (!email) er.email = 'Please enter your company email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) er.email = 'Enter a valid email address';
    else if (!isBusinessEmail(email)) er.email = 'Please use your company email (free providers are not accepted)';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('loading');
    setServerMessage('');
    try {
      await axios.post(`${API}/contact/get-started`, {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim().toLowerCase(),
        source,
      });
      setStatus('success');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setServerMessage(typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
      setStatus('error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-950 border border-neutral-800 text-white sm:max-w-[480px] p-0 overflow-hidden">
        <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
        <div className="p-7">
          {status === 'success' ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-blue-500/15 border border-blue-500/40 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-blue-400" />
              </div>
              <h2 className="mt-5 text-[22px] font-semibold tracking-tight">Thanks, {form.name.split(' ')[0]}!</h2>
              <p className="mt-2 text-[14px] text-neutral-400 leading-relaxed">
                We&rsquo;ve received your request. A member of our team will reach out within one business day at
                {' '}<span className="text-white">{form.email}</span>.
              </p>
              <Button
                onClick={() => onOpenChange(false)}
                className="mt-6 h-10 px-5 rounded-full bg-white text-neutral-950 hover:bg-neutral-200 text-[14px] font-medium"
              >
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader className="space-y-2">
                <p className="text-[12px] uppercase tracking-[0.18em] text-blue-400">MetaxuOne</p>
                <DialogTitle className="text-[24px] leading-tight font-semibold tracking-tight">
                  {title || 'Get started with MetaxuOne'}
                </DialogTitle>
                <DialogDescription className="text-[14px] text-neutral-400 leading-relaxed">
                  {subtitle || 'Tell us a little about you and we’ll be in touch to schedule a personalized walkthrough.'}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="gs-name" className="text-[13px] text-neutral-300">Full name *</Label>
                  <Input
                    id="gs-name"
                    autoComplete="name"
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Jane Doe"
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11"
                  />
                  {errors.name && <p className="text-[12px] text-red-400">{errors.name}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="gs-company" className="text-[13px] text-neutral-300">Company *</Label>
                  <Input
                    id="gs-company"
                    autoComplete="organization"
                    value={form.company}
                    onChange={update('company')}
                    placeholder="Acme Inc."
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11"
                  />
                  {errors.company && <p className="text-[12px] text-red-400">{errors.company}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="gs-email" className="text-[13px] text-neutral-300">Company email *</Label>
                  <Input
                    id="gs-email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={update('email')}
                    placeholder="jane@acme.com"
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11"
                  />
                  {errors.email ? (
                    <p className="text-[12px] text-red-400">{errors.email}</p>
                  ) : (
                    <p className="text-[11.5px] text-neutral-500">Free email providers (Gmail, Yahoo, etc.) are not accepted.</p>
                  )}
                </div>

                {status === 'error' && (
                  <div className="flex items-start gap-2 rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[13px] text-red-300 leading-snug">{serverMessage}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full h-11 mt-2 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Submit request <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>

                <p className="text-[11.5px] text-neutral-500 leading-snug pt-1">
                  By submitting, you agree to be contacted by MetaxuOne and accept our{' '}
                  <a href="/privacy" className="underline hover:text-neutral-300">Privacy Policy</a>.
                </p>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GetStartedDialog;
